import { P2GBox, P2GCardContainer, P2GGrid, P2GText } from '@parcel2go/core-ui';
import { AddressProps } from './address';
import RequirementsField from '~/components/core/forms/form-field-with-named-requirement';
import { BasketSections } from '~/lib/types/enums';
import AddressContactName from './contact-name';
import { AlgoliaSearchObjectAddress } from '~/lib/types/algolia-types';
import { useState } from 'react';
import { Upsell } from '~/lib/types/extra-types';
import SupportingLabel from '~/components/supporting-label/supporting-label';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import UseDetails from './use-details';
import FavouriteAddress from './favourite';
import { IconBox } from '~/components/core/icon-box/icon-box';

type AddressOverviewProps = {
  onAutoComplete: (address: AlgoliaSearchObjectAddress) => void;
  onEdit?: () => void;
  canEdit: boolean;
  isAuthenticated: boolean;
} & AddressProps;

export default function AddressOverview({
  section,
  requirements,
  type,
  errors,
  onAutoComplete,
  forceSelection,
  availableExtras,
  onUseOriginDetails,
  usableOriginDetails,
  canEdit,
  isAuthenticated,
  onEdit,
  disabledFields
}: AddressOverviewProps) {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const [nameExistsOnLoad] = useState(section.data.contactname && section.data.contactname.length > 1);

  return (
    <P2GGrid container spacing={2} direction="column" dataTestId="address-overview">
      {(!nameExistsOnLoad || forceSelection) && (
        <P2GGrid item>
          <AddressContactName
            requirements={requirements}
            address={section.data}
            onChange={(value) => section.update(value)}
            errors={errors}
            loading={section.loading}
            type={type}
            onAutoComplete={onAutoComplete}
            disabled={disabledFields.includes('contactname')}
            disableSearch={!!forceSelection}
          />
        </P2GGrid>
      )}
      {forceSelection && (
        <P2GGrid item>
          <RequirementsField
            requirements={requirements}
            name="organisation"
            id={`txt-${type}-organisation`}
            translationKey={`ADDRESS_FIELD_${type}_ORGANISATION_LABEL`.toUpperCase()}
            defaultLabel="Organisation"
            data={section.data?.organisation ?? ''}
            update={(value) => section.update({ ...section.data, organisation: value })}
            errors={errors}
            loading={section.loading}
            disabled={disabledFields.includes('organisation')}
          />
        </P2GGrid>
      )}
      <P2GGrid item onClick={onEdit}>
        <P2GBox style={canEdit ? { cursor: 'pointer' } : {}}>
          <P2GCardContainer dataTestId="address-overview-container" data-sentry-mask>
            <IconBox icon={isAuthenticated && <FavouriteAddress section={section} type={type} />}>
              {!forceSelection && section.data.contactname && (
                <P2GText component="p" colour="primary">
                  {section.data.contactname}
                </P2GText>
              )}
              {section.data.organisation && <P2GText component="p">{section.data.organisation}</P2GText>}
              <P2GText component="p" dataTestId="address-overview-container-street">
                {section.data.property !== section.data.organisation && <>{section.data.property} </>}
                {section.data.street}
              </P2GText>
              <P2GText component="p">{section.data.town}</P2GText>
              {section.data.county && <P2GText component="p">{section.data.county}</P2GText>}
              <P2GText component="p" dataTestId="address-overview-container-postcode">
                {section.data.postcode}
              </P2GText>
            </IconBox>
          </P2GCardContainer>
        </P2GBox>
      </P2GGrid>
      <P2GGrid item>
        <RequirementsField
          requirements={requirements}
          name="email"
          id={`txt-${type}-email`}
          translationKey={`ADDRESS_FIELD_${type}_EMAIL_LABEL`.toUpperCase()}
          defaultLabel="Email"
          data={section.data?.email ?? ''}
          update={(value) => section.update({ ...section.data, email: value })}
          errors={errors}
          loading={section.loading}
          disabled={disabledFields.includes('email')}
          autoComplete={type === BasketSections.ORIGIN ? 'email' : 'off'}
        />
      </P2GGrid>

      {usableOriginDetails?.includes('email') && (
        <UseDetails
          field={'email'}
          wrapper={(x) => (
            <P2GGrid item xs={12}>
              {x}
            </P2GGrid>
          )}
          usableOriginDetails={usableOriginDetails}
          onUseOriginDetails={onUseOriginDetails}
        />
      )}

      <P2GGrid item>
        <RequirementsField
          requirements={requirements}
          name="phone"
          id={`txt-${type}-phone`}
          translationKey={
            requirements.rules?.some((x) => x.key === 'phone' && x.name === 'Mobile')
              ? `ADDRESS_FIELD_${type}_MOBILE_LABEL`.toUpperCase()
              : `ADDRESS_FIELD_${type}_PHONE_LABEL`.toUpperCase()
          }
          disabled={disabledFields.includes('phone')}
          data={section.data?.phone ?? ''}
          update={(value) => section.update({ ...section.data, phone: value })}
          errors={errors}
          loading={section.loading}
          autoComplete={type === BasketSections.ORIGIN ? 'tel' : 'off'}
        />
      </P2GGrid>

      {usableOriginDetails?.includes('phone') && (
        <UseDetails
          field={'phone'}
          wrapper={(x) => (
            <P2GGrid item xs={12}>
              {x}
            </P2GGrid>
          )}
          usableOriginDetails={usableOriginDetails}
          onUseOriginDetails={onUseOriginDetails}
        />
      )}

      {type === BasketSections.ORIGIN && availableExtras.includes(Upsell.SMS) && (
        <P2GGrid item>
          <SupportingLabel
            text={t('ADDRESS_OVERVIEW_ORIGIN_PHONE_SUPPORTING_TEXT', {
              defaultValue:
                'This service offers an option to receive a text message on delivery and so you will need to enter a mobile number for this.'
            })}
          />
        </P2GGrid>
      )}
    </P2GGrid>
  );
}
